import get from 'lodash.get';

import { emptyArray } from 'ui-components/default-props/empty';
import * as ShipmentsActions from '../../../store/shipments/shipments.actions';
import * as BatchActions from '../../../store/batches/batches.actions';
import { baseReducer as BaseReducer } from '../../../store/core/base-reducer';
import * as BatchNoteActions from '../../../store/batches/notes/batch-note.actions';
import * as Actions from './batch.details.actions';

export const ADD_TO_NEW_BATCH = 'ADD_TO_NEW_BATCH';

const initialState = {
  batch: {},
  isLoading: false,
  isBlockedFromBatch: false,
  // If adding fulfillmentPlans to an existing batch, value will be fulfillmentPlanSetId
  // new batch => 'ADD_TO_NEW_BATCH', not adding => null
  batchReceivingFulfillmentPlans: null,
  moveToBatchModalId: undefined,
  addressValidationIsProcessing: false,
  addressValidationJobs: [],
  selectedViewId: 'all',
  rateBatchIsProcessing: false,
  rateBatchJobs: [],
  shipmentNotificationJobs: [],
  hasNameConflict: false,
};

const makeSetIsLoading = (isLoading) => {
  return (state) => ({
    ...state,
    isLoading,
  });
};

const setIsProcessingAddressValidation = (addressValidationIsProcessing) => {
  return (state) => ({
    ...state,
    addressValidationIsProcessing,
  });
};

const setIsRateBatchValidation = (rateBatchIsProcessing) => {
  return (state) => ({
    ...state,
    rateBatchIsProcessing,
  });
};

const addShipmentNotificationJobs =
  () =>
  (state, { jobGuids }) => {
    const stateJobs = get(state, 'shipmentNotificationJobs', emptyArray);
    return {
      ...state,
      shipmentNotificationJobs: stateJobs.concat(jobGuids),
    };
  };

const removeShipmentNotificationJob =
  () =>
  (state, { jobGuid }) => {
    const stateJobs = get(state, 'shipmentNotificationJobs', emptyArray);
    const shipmentNotificationJobs = stateJobs.filter((job) => job !== jobGuid);
    return {
      ...state,
      shipmentNotificationJobs,
    };
  };

// TODO: handle validation and rate processing in their own batch aware state
const actionHandlerMap = {
  [Actions.GET_BATCH_DETAILS_SUCCESS]: (state, { batch }) => {
    return {
      ...state,
      batch,
      addressValidationIsProcessing: false,
      addressValidationJobs: emptyArray,
      rateBatchIsProcessing: false,
      rateBatchJobs: emptyArray,
      shipmentNotificationJobs: emptyArray,
      isLoading: true,
    };
  },
  [BatchActions.ADDED_FULFILLMENT_PLANS_TO_CREATED_BATCH_SUCCESS]: (state) => {
    return {
      ...makeSetIsLoading(true)(state),
      batchReceivingFulfillmentPlans: state.isBlockedFromBatch ? state.batchReceivingFulfillmentPlans : null,
      isBlockedFromBatch: false,
    };
  },
  [BatchActions.ADD_FULFILLMENT_PLANS_TO_BATCH_SUCCESS]: (state) => {
    return {
      ...state,
      batchReceivingFulfillmentPlans: state.isBlockedFromBatch ? state.batchReceivingFulfillmentPlans : null,
      isBlockedFromBatch: false,
    };
  },
  [BatchActions.FULFILLMENT_PLANS_BLOCKED_FROM_BATCH]: (state) => {
    return {
      ...state,
      isBlockedFromBatch: true,
    };
  },
  [BatchActions.CANCEL_BLOCKED_ADD_TO_BATCH]: (state) => {
    return {
      ...state,
      batchReceivingFulfillmentPlans: null,
      isBlockedFromBatch: false,
    };
  },
  [Actions.GET_BATCH_DETAILS_REQUEST]: makeSetIsLoading(true),
  [ShipmentsActions.GET_SHIPMENTS_SUCCESS]: makeSetIsLoading(false),
  [Actions.GET_FILTERED_BATCH_SHIPMENTS_SUCCESS]: (state, { appliedFilters }) => {
    return {
      ...state,
      appliedFilters,
      isLoading: false,
    };
  },
  [Actions.BATCH_DETAILS_SET_IS_LOADING]: (state, { isLoading }) => {
    return {
      ...state,
      isLoading,
    };
  },
  [Actions.EMPTY_BATCH_DETAILS]: (state) => {
    return {
      ...state,
      batch: initialState.batch,
    };
  },
  [Actions.MOVE_TO_BATCH_MODAL_OPENED]: (state, { moveToBatchModalId }) => {
    return {
      ...state,
      moveToBatchModalId,
    };
  },
  [BatchActions.SAVE_BATCH_NAME_CHANGE_SUCCESS]: (state, { batch }) => {
    return {
      ...state,
      batch,
      hasNameConflict: false,
    };
  },
  [BatchActions.SAVE_BATCH_NAME_CHANGE_FAILURE]: (state, { error }) => {
    const nameConflictErrorCode = 'DuplicateBatchName';
    const errors = get(error, 'response.data.Errors', emptyArray);
    const hasNameConflict = errors.some((err) => err.Code === nameConflictErrorCode);
    return {
      ...state,
      hasNameConflict,
    };
  },
  [BatchNoteActions.SAVE_OR_EDIT_BATCH_NOTE_SUCCESS]: (state, { batchId, note }) => {
    if (state.batch.fulfillmentPlanSetId !== batchId) {
      return state;
    }
    return {
      ...state,
      batch: {
        ...state.batch,
        note,
      },
    };
  },
  [Actions.VALIDATE_BATCH_ADDRESSES_REQUEST]: setIsProcessingAddressValidation(true),
  [Actions.VALIDATE_BATCH_ADDRESSES_FAILURE]: setIsProcessingAddressValidation(false),
  [Actions.VALIDATE_BATCH_ADDRESSES_SUCCESS]: (state, { batchData }) => {
    const jobIds = get(batchData, 'jobGuids', emptyArray);
    const addressValidationJobs = jobIds.map((jobId) => ({
      jobId,
      isDone: false,
    }));
    return {
      ...state,
      addressValidationJobs,
    };
  },
  [Actions.VALIDATE_BATCH_ADDRESSES_RESULTS]: (state, { data }) => {
    // TODO: store the results in a collection so we can update individual columns such as recipient
    // addressValidationResults: data,
    const jobId = get(data, 'jobId');
    const isDone = get(data, 'final');
    const stateJobs = get(state, 'addressValidationJobs');
    const addressValidationJobs = stateJobs.map((job) => ({
      ...job,
      isDone: job.jobId === jobId && isDone,
    }));
    const addressValidationIsProcessing = addressValidationJobs.some((job) => !job.isDone);
    return {
      ...state,
      addressValidationIsProcessing,
      addressValidationJobs,
    };
  },
  [Actions.RATE_BATCH_COMPLETED]: setIsRateBatchValidation(false),
  [Actions.RATES_UPDATED_RESULTS]: (state, { data }) => {
    const jobId = get(data, 'jobId');
    const isDone = get(data, 'final');
    const stateJobs = get(state, 'rateBatchJobs', emptyArray);
    const rateBatchJobs = stateJobs.map((job) => ({
      ...job,
      isDone: job.jobId === jobId && isDone,
    }));
    const rateBatchIsProcessing = rateBatchJobs.some((job) => !job.isDone);
    return {
      ...state,
      rateBatchIsProcessing,
      rateBatchJobs,
    };
  },
  [Actions.SEND_SHIPMENT_NOTIFICATIONS_SUCCESS]: addShipmentNotificationJobs(),
  [Actions.SHIPMENT_NOTIFICATIONS_SENT_SUCCESS]: removeShipmentNotificationJob(),
  [Actions.SHIPMENT_NOTIFICATIONS_SENT_FAILURE]: removeShipmentNotificationJob(),
  [BatchActions.ADD_FULFILLMENT_PLANS_OR_SALES_ORDERS_TO_BATCH_REQUEST]: (state, { fulfillmentPlanSetId }) => {
    return {
      ...state,
      batchReceivingFulfillmentPlans: fulfillmentPlanSetId,
    };
  },
  [BatchActions.CREATE_BATCH_WITH_FULFILLMENT_PLANS_REQUEST]: (state) => {
    return {
      ...state,
      batchReceivingFulfillmentPlans: ADD_TO_NEW_BATCH,
    };
  },
};

export const ConnectedActionHandlerMap = BaseReducer(initialState, actionHandlerMap);

export { namespace } from './batch.details.reducer.metadata';
